import React from 'react';
import PropTypes from 'prop-types';
import {
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  CodeBracketSquareIcon,
  FingerPrintIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import packageJson from '../../package.json';
import ButtonComponent from 'components/common/ButtonComponent';
import SectionIntro from 'components/common/SectionIntro';

const getBuildDate = () => {
  if (process.env.REACT_APP_BUILD_DATE) {
    try {
      const date = new Date(process.env.REACT_APP_BUILD_DATE);

      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    } catch (error) {
      console.warn(
        'Invalid date format in REACT_APP_BUILD_DATE:',
        process.env.REACT_APP_BUILD_DATE
      );
    }
  }

  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const iconToneMap = {
  accent: 'workspace-help-panel-icon',
  info: 'workspace-help-panel-icon workspace-help-panel-icon--info',
  warning: 'workspace-help-panel-icon workspace-help-panel-icon--warning',
  neutral: 'workspace-help-panel-icon workspace-help-panel-icon--neutral'
};

const HelpPanel = ({
  icon: Icon,
  title,
  description,
  children,
  className = '',
  tone = 'accent'
}) => (
  <section className={`workspace-report-surface rounded-md border p-6 ${className}`}>
    <div className="flex items-start gap-4">
      <div
        className={`${iconToneMap[tone]} flex h-12 w-12 shrink-0 items-center justify-center rounded-md`}
      >
        <Icon className="h-6 w-6" />
      </div>

      <div className="min-w-0 flex-1">
        <h2 className="workspace-help-panel-title font-command-display text-2xl font-bold">
          {title}
        </h2>
        {description && (
          <p className="workspace-help-panel-text mt-2 text-sm leading-6">{description}</p>
        )}
        <div className="mt-5">{children}</div>
      </div>
    </div>
  </section>
);

HelpPanel.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  tone: PropTypes.oneOf(['accent', 'info', 'warning', 'neutral'])
};

HelpPanel.defaultProps = {
  description: null,
  children: null,
  className: '',
  tone: 'accent'
};

const Help = () => {
  const { t } = useTranslation();

  const quickStartItems = [
    t('help.quickStart.setupFarm'),
    t('help.quickStart.manageWorkers'),
    t('help.quickStart.recordActivities'),
    t('help.quickStart.viewDashboards')
  ];

  const faqItems = [
    { question: t('help.faqQ1'), answer: t('help.faqA1') },
    { question: t('help.faqQ2'), answer: t('help.faqA2') }
  ];

  const systemInfoItems = [
    { label: t('help.version'), value: packageJson.version },
    { label: t('help.lastUpdated'), value: getBuildDate() }
  ];

  const privacyItems = t('help.privacyProtocol.items', { returnObjects: true });
  const apiEndpoints = t('help.apiDocumentation.endpoints', { returnObjects: true });

  return (
    <div className="workspace-help-page px-4 py-8 sm:px-4">
      <SectionIntro
        kicker={t('dashboard.sidebarHelp')}
        title={t('help.title')}
        description={t('help.subtitle')}
      />

      <div className="grid gap-6 xl:grid-cols-3">
        {/* Contact Support */}
        <HelpPanel
          icon={ChatBubbleLeftRightIcon}
          title={t('help.contactSupport')}
          description={t('help.contactSupportDescription')}
          className="xl:col-span-2"
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="workspace-help-panel-text text-sm leading-6">
              {t('help.sendMessage')}
            </div>
            <ButtonComponent type="button">
              <ChatBubbleLeftRightIcon className="h-4 w-4" />
              {t('help.sendMessage')}
            </ButtonComponent>
          </div>
        </HelpPanel>

        {/* System Info */}
        <HelpPanel
          icon={InformationCircleIcon}
          title={t('help.systemInfo')}
          tone="neutral"
          description={t('help.systemInfoDescription')}
        >
          <div className="space-y-4">
            {systemInfoItems.map(item => (
              <div
                key={item.label}
                className="workspace-help-panel-row flex items-center justify-between gap-4"
              >
                <span className="font-command-label text-[10px] uppercase tracking-[0.18em] workspace-help-panel-label">
                  {item.label}
                </span>
                <span className="workspace-help-panel-value text-sm font-semibold">
                  {item.value}
                </span>
              </div>
            ))}

            <div className="workspace-help-panel-row flex items-center justify-between gap-4">
              <span className="font-command-label text-[10px] uppercase tracking-[0.18em] workspace-help-panel-label">
                {t('help.status')}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-command-label uppercase tracking-[0.16em] workspace-help-status-pill">
                <span className="workspace-help-status-dot h-2 w-2 rounded-full" />
                {t('help.online')}
              </span>
            </div>
          </div>
        </HelpPanel>

        {/* Quick Start Guide */}
        <HelpPanel
          icon={BookOpenIcon}
          title={t('help.quickStartGuide')}
          description={t('help.quickStartDescription')}
          tone="info"
        >
          <ul className="space-y-3">
            {quickStartItems.map(item => (
              <li key={item} className="workspace-help-list-item flex items-start gap-3 text-sm">
                <span className="workspace-help-list-marker mt-1.5 h-2 w-2 rounded-full" />
                <span className="workspace-help-panel-text">{item}</span>
              </li>
            ))}
          </ul>
        </HelpPanel>

        {/* API Documentation */}
        <HelpPanel
          icon={CodeBracketSquareIcon}
          title={t('help.apiDocumentation.title')}
          description={t('help.apiDocumentation.description')}
          tone="info"
          className="xl:col-span-2"
        >
          <div className="space-y-4">
            <div className="workspace-help-faq-item rounded-md p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="font-command-label text-[10px] uppercase tracking-[0.18em] workspace-help-panel-label">
                  {t('help.apiDocumentation.authentication.label')}
                </span>
                <span className="workspace-help-panel-value text-sm font-semibold">
                  {t('help.apiDocumentation.authentication.value')}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {apiEndpoints.map(endpoint => (
                <div
                  key={`${endpoint.method}-${endpoint.path}`}
                  className="workspace-help-faq-item rounded-md p-4"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="workspace-help-status-pill rounded-full px-3 py-1 font-command-label text-[10px] uppercase tracking-[0.16em]">
                          {endpoint.method}
                        </span>
                        <code className="workspace-help-panel-value break-all text-sm font-semibold">
                          {endpoint.path}
                        </code>
                      </div>
                      <p className="workspace-help-panel-text mt-2 text-sm leading-6">
                        {endpoint.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </HelpPanel>

        {/* Privacy Protocol */}
        <HelpPanel
          icon={FingerPrintIcon}
          title={t('help.privacyProtocol.title')}
          description={t('help.privacyProtocol.description')}
          className="xl:col-span-2"
        >
          <ul className="space-y-3">
            {privacyItems.map(item => (
              <li key={item} className="workspace-help-list-item flex items-start gap-3 text-sm">
                <span className="workspace-help-list-marker mt-1.5 h-2 w-2 rounded-full" />
                <span className="workspace-help-panel-text">{item}</span>
              </li>
            ))}
          </ul>
        </HelpPanel>

        {/* FAQ */}
        <HelpPanel
          icon={QuestionMarkCircleIcon}
          title={t('help.faq')}
          description={t('help.faqDescription')}
          tone="warning"
        >
          <div className="space-y-4">
            {faqItems.map(({ question, answer }) => (
              <div key={question} className="workspace-help-faq-item rounded-md p-4">
                <p className="workspace-help-panel-title text-base font-semibold">{question}</p>
                <p className="workspace-help-panel-text mt-2 text-sm leading-6">{answer}</p>
              </div>
            ))}
          </div>
        </HelpPanel>
      </div>
    </div>
  );
};

Help.propTypes = {};

export default Help;
